import React, { useState, useEffect } from 'react';
import './CollapsibleTable.css';
import { FaEdit, FaAngleDoubleLeft, FaAngleDoubleRight, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { Drawer, Box, Stack, Divider, IconButton, Grid, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import MaterialTypography from '../MaterialTypography';
import MaterialButton from '../MaterialButton';
import MaterialSelect from '../MaterialSelect';
import MaterialInput from '../MaterialInput';
import { BASE_URL } from '../../pages/Utils/Baseurl';
import axios from 'axios';
import { useSnackbar } from '../SnackbarProvider';

const CollapsibleTable = ({
    columns,
    data,
    project_id,
    onEdit,
    serverSide = false,
    totalCount = 0,
    page = 1,
    pageSize = 10,
    onPageChange,
    onPageSizeChange,
    onSort,
    sortConfig,
}) => {
    const { showSnackbar } = useSnackbar();
    const [currentPage, setCurrentPage] = useState(serverSide ? page : 1);
    const [rowsPerPage, setRowsPerPage] = useState(serverSide ? pageSize : 10);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [reallocateMode, setReallocateMode] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [employeeOptions, setEmployeeOptions] = useState([]);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [reallocationSuccess, setReallocationSuccess] = useState(false);
    const [historyData, setHistoryData] = useState([]);


    useEffect(() => {
        if (serverSide) {
            setCurrentPage(page);
            setRowsPerPage(pageSize);
        }
    }, [page, pageSize, serverSide]);

    useEffect(() => {
        console.log('Employee options updated:', employeeOptions);
    }, [employeeOptions]);

    const displayColumns = columns.filter(col => col.showDisplay === true);

    const totalRows = serverSide ? totalCount : data.length;
    const totalPages = Math.ceil(totalRows / rowsPerPage);

    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;

    const currentData = serverSide ? data : data.slice(startIndex, endIndex);

    const fetchEmployeesReallocation = async () => {
        try {
            console.log('Fetching employees for project_id:', project_id);
            const token = sessionStorage.getItem("token");
            const response = await axios.get(`${BASE_URL}api/project_employees/`, {
                params: { project_id: project_id },
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });
            console.log('Employee reallocation API response:', response.data);

            const employeeOpts = response.data?.data?.map(item => ({
                value: item.id,
                label: item.employee_name
            })) || [];

            console.log('Transformed employee options:', employeeOpts);
            setEmployeeOptions(employeeOpts);
        } catch (error) {
            console.log('Error fetching employees for reallocation:', error);
            setEmployeeOptions([]);
        }
    }

    const fetchTaskHistory = async (taskId) => {
        try {
            const token = sessionStorage.getItem("token");
            const response = await axios.get(`${BASE_URL}api/task_reallocate/`, {
                params: { task_id: taskId },
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });
            console.log('Task history response:', response.data);
            if (response.data?.status) {
                setHistoryData(response.data.data || []);
            } else {
                setHistoryData([]);
            }
        } catch (error) {
            console.error('Error fetching task history:', error);
            setHistoryData([]);
        }
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            if (serverSide && onPageChange) {
                onPageChange(page);
            }
        }
    };

    const handleRowsPerPageChange = (e) => {
        const newPageSize = Number(e.target.value);
        setRowsPerPage(newPageSize);
        setCurrentPage(1);
        if (serverSide && onPageSizeChange) {
            onPageSizeChange(newPageSize);
        }
    };

    const getPageNumbers = () => {
        const pages = [];
        const maxPagesToShow = 5;

        if (totalPages <= maxPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (currentPage <= 3) {
                for (let i = 1; i <= 4; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
            } else {
                pages.push(1);
                pages.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            }
        }
        return pages;
    };

    const renderCellContent = (content) => {
        if (content === null || content === undefined || content === '') return '-';
        const str = String(content);
        if (str.length > 17) {
            return <span title={str}>{str.substring(0, 17)}...</span>;
        }
        return str;
    };

    const handleActionClick = (row) => {
        setSelectedRow(row);
        setReallocateMode(false);
        setSelectedEmployee(null);
        setDrawerOpen(true);
        fetchTaskHistory(row.id);
    };

    const handleDrawerClose = () => {
        setDrawerOpen(false);
        setSelectedRow(null);
        setReallocateMode(false);
        setSelectedEmployee(null);
        setReallocationSuccess(false);
    };

    const handleReallocateClick = () => {
        console.log('Reallocate clicked, project_id:', project_id);
        setReallocateMode(true);
        if (project_id) {
            fetchEmployeesReallocation();
        } else {
            console.warn('No project_id available for fetching employees');
        }
    };

    const handleCancelReallocate = () => {
        setReallocateMode(false);
        setSelectedEmployee(null);
    };

    const handleCancelTask = async () => {
        if (!selectedRow) {
            showSnackbar('No task selected', 'error');
            return;
        }

        if (!cancelReason || cancelReason.trim() === '') {
            showSnackbar('Please enter a cancellation reason', 'error');
            return;
        }

        try {
            console.log('Cancelling task ID:', selectedRow.id);
            const token = sessionStorage.getItem("token");
            const payload = {
                status: 'Cancel',
                actual_hours: '0',
                cancel_reason: cancelReason
            };

            const response = await axios.post(
                `${BASE_URL}api/updatetaskstatus/${selectedRow.id}/`,
                payload,
                {
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            console.log('Cancel task response:', response.data);

            if (response.data?.status || response.status === 200) {
                showSnackbar(response.data?.message || 'Task cancelled successfully', 'success');

                setSelectedRow({ ...selectedRow, status: 'Cancel' });

                setCancelDialogOpen(false);
                setCancelReason('');
            } else {
                showSnackbar(response.data?.message || 'Failed to cancel task', 'error');
            }
        } catch (error) {
            console.error('Error cancelling task:', error);
            const errorMessage = error?.response?.data?.message || error?.message || 'An error occurred while cancelling task';
            showSnackbar(errorMessage, 'error');
        }
    };

    const handleOpenCancelDialog = () => {
        setCancelDialogOpen(true);
    };

    const handleCloseCancelDialog = () => {
        setCancelDialogOpen(false);
        setCancelReason('');
    };

    const handleSubmitReallocate = async () => {
        if (!selectedEmployee) {
            showSnackbar('Please select an employee', 'error');
            return;
        }

        if (!selectedRow) {
            showSnackbar('No task selected', 'error');
            return;
        }

        try {
            console.log('Reallocating task ID:', selectedRow.id, 'to employee:', selectedEmployee.value);
            const token = sessionStorage.getItem("token");
            const payload = {
                task_id: selectedRow.id,
                new_assignee: selectedEmployee.value
            };

            const response = await axios.post(
                `${BASE_URL}api/task_reallocate/`,
                payload,
                {
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            console.log('Reallocation API response:', response.data);

            if (response.data?.status || response.status === 200) {
                showSnackbar(response.data?.message || 'Task reallocated successfully', 'success');
                setReallocationSuccess(true);
                setReallocateMode(false);
            } else {
                showSnackbar(response.data?.message || 'Failed to reallocate task', 'error');
            }
        } catch (error) {
            console.error('Error reallocating task:', error);
            const errorMessage = error?.response?.data?.message || error?.message || 'An error occurred while reallocating task';
            showSnackbar(errorMessage, 'error');
        }
    };

    const DrawerItem = ({ label, value }) => (
        <Stack spacing={0.5}>
            <MaterialTypography variant="caption" color="dark" fontWeight="bold">
                {label}
            </MaterialTypography>
            <MaterialTypography variant="caption" color="text" style={{ wordBreak: 'break-word' }}>
                {value || '-'}
            </MaterialTypography>
        </Stack>
    );

    return (
        <div className="collapsible-table-wrapper">
            <div className="collapsible-table-container">
                <table className="collapsible-table">
                    <thead>
                        <tr>
                            <th className="action-header">Action</th>
                            {displayColumns.map((col, index) => (
                                <th
                                    key={index}
                                    onClick={() => onSort && onSort(col.key)}
                                    style={{ cursor: onSort ? 'pointer' : 'default' }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <span>{col.header}</span>
                                        {sortConfig && sortConfig.key === col.key && (
                                            <span style={{ fontSize: '10px' }}>
                                                {sortConfig.direction === 'asc' ? '▲' : '▼'}
                                            </span>
                                        )}
                                    </Stack>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {currentData.map((row) => (
                            <tr key={row.id} className="main-row">
                                <td className="action-cell-icon" onClick={() => handleActionClick(row)}>
                                    <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ cursor: 'pointer' }}>
                                        <path d="M2 12.5C2 6.97766 6.47729 2.50098 11.9996 2.50098C17.5218 2.50098 21.9991 6.97766 21.9991 12.5C21.9991 18.0224 17.5218 22.4991 11.9996 22.4991C6.47729 22.4991 2 18.0224 2 12.5ZM12.5016 9.15521C12.2213 9.27126 12.0385 9.54477 12.0385 9.84817L12.0385 11.749L8.55957 11.749C8.14536 11.749 7.80957 12.0847 7.80957 12.499C7.80957 12.9132 8.14536 13.249 8.55957 13.249H12.0385V15.152C12.0385 15.4554 12.2213 15.7289 12.5016 15.8449C12.7819 15.961 13.1046 15.8967 13.319 15.6821L15.9572 13.0423C16.1007 12.9057 16.1902 12.7128 16.1902 12.499C16.1902 12.2725 16.0898 12.0694 15.9311 11.9319L13.319 9.31802C13.1046 9.10342 12.782 9.03915 12.5016 9.15521Z" fill="#323544" />
                                    </svg>
                                </td>
                                {displayColumns.map((col, index) => (
                                    <td key={index}>{renderCellContent(row[col.key])}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
                {currentData.length === 0 && (
                    <div className="no-data">
                        <p>No data available</p>
                    </div>
                )}
            </div>

            {data.length > 0 && (
                <div className="pagination-wrapper">
                    <div className="pagination-info">
                        <span className="rows-info">
                            Showing {startIndex + 1} to {Math.min(endIndex, totalRows)} of {totalRows} entries
                        </span>
                        <div className="rows-per-page">
                            <label htmlFor="rowsPerPage">Rows per page:</label>
                            <select
                                id="rowsPerPage"
                                value={rowsPerPage}
                                onChange={handleRowsPerPageChange}
                                className="rows-select"
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </div>
                    </div>

                    <div className="pagination-controls">
                        <button className="pagination-btn" onClick={() => handlePageChange(1)} disabled={currentPage === 1}><FaAngleDoubleLeft /></button>
                        <button className="pagination-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}><FaChevronLeft /></button>
                        <div className="page-numbers">
                            {getPageNumbers().map((page, index) => (
                                page === '...' ? <span key={`ellipsis-${index}`} className="page-ellipsis">...</span> :
                                    <button key={page} className={`page-number ${currentPage === page ? 'active' : ''}`} onClick={() => handlePageChange(page)}>{page}</button>
                            ))}
                        </div>
                        <button className="pagination-btn" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}><FaChevronRight /></button>
                        <button className="pagination-btn" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}><FaAngleDoubleRight /></button>
                    </div>
                </div>
            )}

            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={handleDrawerClose}
                PaperProps={{
                    sx: { width: 500, display: 'flex', flexDirection: 'column' }
                }}
            >
                {selectedRow && (
                    <>
                        <Box sx={{ padding: 3, flex: 1, overflowY: 'auto' }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                <MaterialTypography variant="h6" fontWeight="medium">
                                    Task Details
                                </MaterialTypography>
                                {selectedRow?.status === 'Not Started' && (
                                    <Stack direction="row" spacing={1}>
                                        <IconButton size="small" onClick={() => onEdit(selectedRow)}>
                                            <FaEdit />
                                        </IconButton>
                                    </Stack>
                                )}
                            </Stack>
                            <Divider sx={{ mb: 2 }} />

                            <Grid container spacing={2}>
                                <Grid size={{ xs: 6 }}><DrawerItem label="Project ID" value={selectedRow.project_code} /></Grid>
                                <Grid size={{ xs: 6 }}><DrawerItem label="Project Name" value={selectedRow.project_name} /></Grid>
                                <Grid size={{ xs: 6 }}><DrawerItem label="Task ID" value={selectedRow.task_code} /></Grid>
                                <Grid size={{ xs: 6 }}><DrawerItem label="Task Name" value={selectedRow.task_title} /></Grid>
                                <Grid size={{ xs: 6 }}><DrawerItem label="Milestone" value={selectedRow.milestone_name} /></Grid>
                                <Grid size={{ xs: 6 }}><DrawerItem label="Module" value={selectedRow.module} /></Grid>
                                <Grid size={{ xs: 6 }}><DrawerItem label="Team" value={selectedRow.teams} /></Grid>
                                <Grid size={{ xs: 6 }}><DrawerItem label="Employee" value={selectedRow.assignee_name} /></Grid>
                                <Grid size={{ xs: 6 }}><DrawerItem label="Start Date" value={selectedRow.start_date} /></Grid>
                                <Grid size={{ xs: 6 }}><DrawerItem label="End Date" value={selectedRow.end_date} /></Grid>
                                <Grid size={{ xs: 6 }}><DrawerItem label="Assigned Hours" value={selectedRow.assigned_hours} /></Grid>
                                <Grid size={{ xs: 6 }}><DrawerItem label="Actual Start Date" value={selectedRow.actual_start_date} /></Grid>
                                <Grid size={{ xs: 6 }}><DrawerItem label="Actual End Date" value={selectedRow.actual_end_date} /></Grid>
                                <Grid size={{ xs: 6 }}><DrawerItem label="Actual Hours" value={selectedRow.hold_date ? (Number(selectedRow.reason_hours || 0) - Number(selectedRow.actual_hours || 0)) : selectedRow.actual_hours} /></Grid>
                                <Grid size={{ xs: 6 }}><DrawerItem label="Priority" value={selectedRow.priority_level} /></Grid>
                                <Grid size={{ xs: 6 }}><DrawerItem label="Status" value={selectedRow.status} /></Grid>
                                <Grid size={{ xs: 6 }}><DrawerItem label="Stakeholder" value={selectedRow.stake_holder[0].user_name} /></Grid>
                                <Grid size={{ xs: 6 }}><DrawerItem label="Document" value={selectedRow.attachment} /></Grid>
                                <Grid size={{ xs: 12 }}><DrawerItem label="Description" value={selectedRow.task_describtion}/></Grid>
                                {selectedRow.reason && (
                                    <Grid size={{ xs: 12 }}><DrawerItem label="Reason" value={selectedRow.reason} /></Grid>
                                )}
                                {selectedRow.cancel_reason && (
                                    <Grid size={{ xs: 12 }}><DrawerItem label="Task Cancel Reason" value={selectedRow.cancel_reason} /></Grid>
                                )}
                            </Grid>

                            <Box sx={{ mt: 3 }}>
                                <MaterialTypography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    History
                                </MaterialTypography>
                                {historyData && historyData.length > 0 ? (
                                    <Stack spacing={1}>
                                        {historyData.map((item, index) => (
                                            <Stack key={index} direction="column" spacing={0.5} sx={{ p: 1, borderBottom: '1px solid #f0f0f0' }}>
                                                <Stack direction="row" justifyContent="space-between">
                                                    <MaterialTypography variant="caption" color="textSecondary" fontWeight="bold">
                                                        {item.date}
                                                    </MaterialTypography>
                                                </Stack>
                                                <MaterialTypography variant="caption" color="dark">
                                                    Reallocated by <strong>{item.reallocated_by}</strong> from <strong>{item.previous_assignee}</strong>
                                                </MaterialTypography>
                                                {item.reason && (
                                                    <MaterialTypography variant="caption" color="text">
                                                        Reason: {item.reason}
                                                    </MaterialTypography>
                                                )}
                                            </Stack>
                                        ))}
                                    </Stack>
                                ) : (
                                    <MaterialTypography variant="caption" color="text">No history available</MaterialTypography>
                                )}
                            </Box>
                        </Box>

                        {selectedRow?.status === 'Not Started' && (
                            <Box sx={{
                                p: 2,
                                borderTop: '1px solid #e0e0e0',
                                backgroundColor: '#fff',
                                position: 'sticky',
                                bottom: 0,
                                zIndex: 10
                            }}>
                                {reallocateMode ? (
                                    reallocationSuccess ? (
                                        <MaterialTypography variant="body2" color="success" sx={{ textAlign: 'center', py: 1 }}>
                                            Task reallocated successfully!
                                        </MaterialTypography>
                                    ) : (
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Box sx={{ flex: 1 }}>
                                                <MaterialSelect
                                                    size='small'
                                                    placeholder="Select Employee"
                                                    options={employeeOptions}
                                                    value={selectedEmployee}
                                                    onChange={setSelectedEmployee}
                                                    menuPortalTarget={document.body}
                                                    menuPosition="fixed"
                                                    menuPlacement="top"
                                                    styles={{
                                                        menuPortal: base => ({
                                                            ...base,
                                                            zIndex: 9999
                                                        }),
                                                        menu: base => ({
                                                            ...base,
                                                            zIndex: 9999
                                                        })
                                                    }}
                                                />
                                            </Box>
                                            <MaterialButton
                                                variant="contained"
                                                color="dark"
                                                size="small"
                                                onClick={handleSubmitReallocate}
                                            >
                                                Submit
                                            </MaterialButton>
                                            <MaterialButton
                                                variant="outlined"
                                                color="secondary"
                                                size="small"
                                                onClick={handleCancelReallocate}
                                            >
                                                Cancel
                                            </MaterialButton>
                                        </Stack>
                                    )
                                ) : (
                                    <Stack direction="row" spacing={2} justifyContent="space-between">
                                        {selectedRow?.status !== 'Cancel' && (
                                            <MaterialButton
                                                variant="outlined"
                                                color="error"
                                                size="small"
                                                onClick={handleOpenCancelDialog}
                                            >
                                                Cancel the Task
                                            </MaterialButton>
                                        )}
                                        <MaterialButton
                                            variant="contained"
                                            color="dark"
                                            size="small"
                                            onClick={handleReallocateClick}
                                        >
                                            Reallocate the Task
                                        </MaterialButton>
                                    </Stack>
                                )}
                            </Box>
                        )}
                    </>
                )}
            </Drawer>

            <Dialog open={cancelDialogOpen} onClose={handleCloseCancelDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Cancel Task</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <MaterialTypography variant="body2" color="text">
                            Please provide a reason for cancelling this task:
                        </MaterialTypography>
                        <MaterialInput
                            multiline
                            rows={4}
                            placeholder="Enter cancellation reason..."
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <MaterialButton
                        variant="outlined"
                        color="dark"
                        onClick={handleCloseCancelDialog}
                    >
                        Cancel
                    </MaterialButton>
                    <MaterialButton
                        variant="contained"
                        color="error"
                        onClick={handleCancelTask}
                    >
                        Confirm Cancellation
                    </MaterialButton>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default CollapsibleTable;
